/**
 * Module 'templates': mission templates as data (architecture gap analysis 8.4, step 4).
 *
 * A template is a ready-made mission: a goal (what done looks like), instructions for the
 * agent, a few fields the person fills in, and suggested start settings. Orgus ships four
 * built-ins; admins add their own through the hub (`apps/hub/src/ext/templates/`).
 *
 *   GET    /api/templates               { templates }  built-ins first, then custom
 *   POST   /api/templates               create a custom template (admin)
 *   PATCH  /api/templates/:id           change a custom template (admin)
 *   DELETE /api/templates/:id           delete a custom template (admin)
 *   POST   /api/templates/:id/start     render it and run session.start (operator)
 *
 * Browser-safe, no runtime dependencies.
 */
import type { Id, Ts } from '../index.ts';
import { trimBothOf } from './trim.ts';

export type TemplateIcon = 'test' | 'docs' | 'package' | 'bug' | 'sparkles' | 'wrench' | 'shield' | 'rocket';
export const TEMPLATE_ICONS: readonly TemplateIcon[] = ['test', 'docs', 'package', 'bug', 'sparkles', 'wrench', 'shield', 'rocket'];

/** One thing the person fills in. `{key}` in the goal, prompt, mission title or branch is replaced by its value. */
export interface TemplateField {
  key: string;
  label: string;
  placeholder?: string;
  /** One line under the input. */
  hint?: string;
  required: boolean;
  /** Used in place of an empty value. */
  default?: string;
  /** A paragraph rather than one line. */
  multiline?: boolean;
}

export interface MissionTemplate {
  /** Built-ins: a kebab-case name ('fix-failing-tests'). Custom: 'tpl_<ulid>'. */
  id: string;
  /** The card title: 'Fix failing tests'. */
  title: string;
  /** One line on the card: what the agent will do. */
  summary: string;
  icon: TemplateIcon;
  /** The mission's title once filled in, e.g. 'Write docs for {module}'. */
  missionTitle: string;
  /** What done looks like. May use {field}. */
  goal: string;
  /** Instructions for the agent. May use {field}. */
  prompt: string;
  fields: TemplateField[];
  /** Suggested start: headless runs in the background, terminal opens a window on the machine. */
  mode: 'headless' | 'terminal';
  /** Suggested branch for a new worktree, e.g. 'docs/{module}'. Null: run in the checkout itself. */
  branch: string | null;
  tags: string[];
  builtIn: boolean;
  createdBy: string | null;
  createdAt: Ts | null;
  updatedAt: Ts | null;
}

/** POST /api/templates, and PATCH with any subset. */
export interface TemplateInput {
  title: string;
  summary?: string;
  icon?: TemplateIcon;
  missionTitle?: string;
  goal: string;
  prompt?: string;
  fields?: TemplateField[];
  mode?: 'headless' | 'terminal';
  branch?: string | null;
  tags?: string[];
}

/** POST /api/templates/:id/start */
export interface TemplateStartRequest {
  machineId: Id;
  checkoutId?: Id;
  cwd?: string;
  values?: Record<string, string>;
  mode?: 'headless' | 'terminal';
  /** Start in a new worktree on the template's branch (or this one). */
  newWorktree?: { branch: string } | null;
  model?: string;
}

export interface TemplateStartResponse {
  ok: boolean;
  message?: string;
  /** The rendered mission, as the agent received it. */
  rendered: RenderedTemplate;
  auditId?: Id;
}

/** state.ext.templates */
export interface TemplatesState {
  templates: MissionTemplate[];
}

export interface RenderedTemplate {
  templateId: string;
  title: string;
  goal: string;
  /** The instructions with every field filled in. */
  instructions: string;
  /** The suggested worktree branch, made safe for git, or null. */
  branch: string | null;
}

/** Audit labels for this module's actions (machine 'hub'); one MODULE_LABELS line in the web's audit labels. */
export const TEMPLATES_ACTION_LABEL: Record<string, string> = {
  'template.create': 'Add mission template',
  'template.update': 'Change mission template',
  'template.delete': 'Delete mission template',
  'template.start': 'Start mission from template',
};

// ---------------------------------------------------------------------------
// The built-ins
// ---------------------------------------------------------------------------

const builtIn = (t: Omit<MissionTemplate, 'builtIn' | 'createdBy' | 'createdAt' | 'updatedAt'>): MissionTemplate => ({
  ...t,
  builtIn: true,
  createdBy: null,
  createdAt: null,
  updatedAt: null,
});

export const BUILTIN_TEMPLATES: readonly MissionTemplate[] = [
  builtIn({
    id: 'fix-failing-tests',
    title: 'Fix failing tests',
    summary: 'Run the tests, find what fails and fix the code, never the test.',
    icon: 'test',
    missionTitle: 'Fix failing tests',
    goal: 'Every test passes with {command}, and no test was skipped, deleted or weakened to get there.',
    prompt: [
      'Run {command} and list every failing test.',
      'For each failure, find the root cause in the code under test and fix it there. Change a test only when the test itself is wrong, and say why.',
      'Run {command} again after each fix. Stop and report when everything passes, or when a failure needs a decision from a person.',
      'Finish with a short summary: what failed, why, and what you changed.',
    ].join('\n'),
    fields: [
      { key: 'command', label: 'Test command', placeholder: 'pnpm test', hint: "Leave empty to use the project's usual test command.", required: false, default: "the project's test command" },
    ],
    mode: 'headless',
    branch: 'fix/failing-tests',
    tags: ['tests', 'quality'],
  }),
  builtIn({
    id: 'write-docs',
    title: 'Write docs for a module',
    summary: 'Read a module and write the README and doc comments it is missing.',
    icon: 'docs',
    missionTitle: 'Write docs for {module}',
    goal: '{module} has a README that says what it does, how to use it and how to test it, and every exported function and type has a doc comment.',
    prompt: [
      'Read {module} and the code that calls it before writing anything.',
      'Write or update its README: what it is for, a short example, how to run its tests, and anything surprising.',
      'Add doc comments to exported functions and types that lack them. Do not change behaviour.',
      'Keep the language plain and short. Finish with a list of the files you changed.',
    ].join('\n'),
    fields: [{ key: 'module', label: 'Module or folder', placeholder: 'packages/protocol', required: true }],
    mode: 'headless',
    branch: 'docs/{module}',
    tags: ['docs'],
  }),
  builtIn({
    id: 'dependency-bump',
    title: 'Dependency bump',
    summary: 'Update dependencies, read the changelogs, and keep the build green.',
    icon: 'package',
    missionTitle: 'Bump {package}',
    goal: '{package} is on its latest compatible version, the lockfile is updated, and the build and tests pass.',
    prompt: [
      'Update {package} to its latest version that the project can take without a rewrite.',
      'Read the changelog for every version you skip over and adapt the code to any breaking change.',
      'Run the build and the tests. If a major upgrade needs more than small changes, stop and report what it would take instead of forcing it.',
      'Finish with a table: package, old version, new version, notable changes.',
    ].join('\n'),
    fields: [
      { key: 'package', label: 'Package', placeholder: 'react', hint: 'Leave empty for every outdated dependency (minor and patch versions only).', required: false, default: 'every outdated dependency (minor and patch versions only)' },
    ],
    mode: 'headless',
    branch: 'chore/dependency-bump',
    tags: ['dependencies', 'maintenance'],
  }),
  builtIn({
    id: 'bug-triage',
    title: 'Bug triage',
    summary: 'Reproduce a bug, find its cause, and fix it if the fix is small and safe.',
    icon: 'bug',
    missionTitle: 'Triage: {report}',
    goal: 'The cause of the bug is found and explained, a failing test reproduces it, and it is fixed if the fix is small and safe.',
    prompt: [
      'The bug: {report}',
      'Reproduce it first, with a test that fails because of it.',
      'Find the root cause. Explain it in two or three sentences with the file and line.',
      'If the fix is small and safe, make it and show that the new test passes. Otherwise stop and describe the fix you recommend.',
    ].join('\n'),
    fields: [{ key: 'report', label: 'What is wrong', placeholder: 'Saving a project with a space in its name fails with a 500.', required: true, multiline: true }],
    mode: 'headless',
    branch: 'fix/triage',
    tags: ['bugs'],
  }),
];

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const PLACEHOLDER = /\{([a-z][a-z0-9_]*)\}/g;

/** Every {key} a text uses, in order of first use. */
export function placeholdersIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

/** The value each field takes: what the person typed, trimmed, or the field's default. */
export function fieldValues(t: Pick<MissionTemplate, 'fields'>, values: Record<string, string> = {}): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of t.fields) {
    const v = (values[f.key] ?? '').trim();
    out[f.key] = v || f.default || '';
  }
  return out;
}

function fill(text: string, values: Record<string, string>): string {
  return text.replace(PLACEHOLDER, (all, key: string) => (key in values ? values[key]! : all));
}

/** A git branch name from free text: lower case, safe characters, no double slashes or dots. */
export function safeBranch(raw: string): string | null {
  const s = raw
    .toLowerCase()
    .replace(/[^a-z0-9/_.-]+/g, '-')
    .replace(/\.{2,}/g, '.')
    .replace(/\/{2,}/g, '/')
    .replace(/-{2,}/g, '-')
    .split('/')
    .map((part) => trimBothOf(part, '-.').replace(/\.lock$/, ''))
    .filter(Boolean)
    .join('/')
    .slice(0, 80)
    .replace(/[-./]+$/, '');
  return s || null;
}

const oneLine = (s: string, max: number) => {
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 3).trimEnd()}...` : flat;
};

/** The template with every field filled in. */
export function renderTemplate(t: MissionTemplate, values: Record<string, string> = {}): RenderedTemplate {
  const v = fieldValues(t, values);
  // Titles stay one line and short, whatever a field holds.
  const short: Record<string, string> = {};
  for (const [k, x] of Object.entries(v)) short[k] = oneLine(x, 60);
  // Branches take the typed value only: a default sentence makes a poor branch name.
  const typed: Record<string, string> = {};
  for (const f of t.fields) typed[f.key] = (values[f.key] ?? '').trim();
  const branchText = t.branch ? fill(t.branch, typed).replace(PLACEHOLDER, '') : null;
  return {
    templateId: t.id,
    title: oneLine(fill(t.missionTitle || t.title, short), 120) || t.title,
    goal: fill(t.goal, v).trim(),
    instructions: fill(t.prompt, v).trim(),
    branch: branchText == null ? null : safeBranch(branchText),
  };
}

/** The prompt a session receives when no missions module wraps it: the goal, then the instructions. */
export function templatePrompt(r: RenderedTemplate): string {
  return [`Goal: ${r.goal}`, r.instructions].filter(Boolean).join('\n\n');
}

// ---------------------------------------------------------------------------
// Validation (the hub and the browser use the same rules)
// ---------------------------------------------------------------------------

export const TEMPLATE_TITLE_MAX = 80;
export const TEMPLATE_SUMMARY_MAX = 160;
export const TEMPLATE_GOAL_MAX = 4_000;
export const TEMPLATE_PROMPT_MAX = 8_000;
export const TEMPLATE_FIELDS_MAX = 6;
export const TEMPLATE_VALUE_MAX = 2_000;
export const TEMPLATES_CUSTOM_MAX = 200;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const FIELD_KEY = /^[a-z][a-z0-9_]{0,31}$/;

export function templateFieldError(f: unknown, i: number): string | null {
  const n = `Field ${i + 1}`;
  if (!isObj(f)) return `${n} must be an object.`;
  if (typeof f.key !== 'string' || !FIELD_KEY.test(f.key)) return `${n} needs a key of lower-case letters, digits and _, starting with a letter.`;
  if (typeof f.label !== 'string' || !f.label.trim() || f.label.length > 60) return `${n} needs a label under 60 characters.`;
  for (const k of ['placeholder', 'hint', 'default'] as const) {
    if (f[k] !== undefined && (typeof f[k] !== 'string' || (f[k] as string).length > 200)) return `${n}: ${k} must be text under 200 characters.`;
  }
  if (f.required !== undefined && typeof f.required !== 'boolean') return `${n}: required must be true or false.`;
  if (f.multiline !== undefined && typeof f.multiline !== 'boolean') return `${n}: multiline must be true or false.`;
  return null;
}

/** Why a template body is not acceptable, or null. `partial` checks only what is present (PATCH). */
export function templateInputError(v: unknown, partial = false): string | null {
  if (!isObj(v)) return 'Send the template as a JSON object.';
  const has = (k: string) => !partial || k in v;
  if (has('title') && (typeof v.title !== 'string' || !v.title.trim() || v.title.length > TEMPLATE_TITLE_MAX)) {
    return `Give the template a title under ${TEMPLATE_TITLE_MAX} characters.`;
  }
  if ('summary' in v && v.summary !== undefined && (typeof v.summary !== 'string' || v.summary.length > TEMPLATE_SUMMARY_MAX)) {
    return `Keep the summary under ${TEMPLATE_SUMMARY_MAX} characters.`;
  }
  if ('missionTitle' in v && v.missionTitle !== undefined && (typeof v.missionTitle !== 'string' || v.missionTitle.length > 200)) {
    return 'Keep the mission title under 200 characters.';
  }
  if (has('goal') && (typeof v.goal !== 'string' || !v.goal.trim() || v.goal.length > TEMPLATE_GOAL_MAX)) {
    return 'Say what done looks like: the goal.';
  }
  if ('prompt' in v && v.prompt !== undefined && (typeof v.prompt !== 'string' || v.prompt.length > TEMPLATE_PROMPT_MAX)) {
    return `Keep the instructions under ${TEMPLATE_PROMPT_MAX} characters.`;
  }
  if ('icon' in v && v.icon !== undefined && !TEMPLATE_ICONS.includes(v.icon as TemplateIcon)) return 'That icon is not one Orgus offers.';
  if ('mode' in v && v.mode !== undefined && v.mode !== 'headless' && v.mode !== 'terminal') return 'The mode must be headless or terminal.';
  if ('branch' in v && v.branch !== undefined && v.branch !== null && (typeof v.branch !== 'string' || v.branch.length > 80 || /\s|\.\.|[~^:?*[\\]/.test(v.branch))) {
    return 'The branch must be a git branch name without spaces (it may use {field}).';
  }
  if ('tags' in v && v.tags !== undefined && (!Array.isArray(v.tags) || v.tags.length > 8 || v.tags.some((t) => typeof t !== 'string' || !t.trim() || t.length > 30))) {
    return 'Use at most 8 tags, each under 30 characters.';
  }
  let keys: string[] | null = null;
  if ('fields' in v && v.fields !== undefined) {
    if (!Array.isArray(v.fields) || v.fields.length > TEMPLATE_FIELDS_MAX) return `A template has at most ${TEMPLATE_FIELDS_MAX} fields.`;
    for (let i = 0; i < v.fields.length; i++) {
      const e = templateFieldError(v.fields[i], i);
      if (e) return e;
    }
    keys = v.fields.map((f) => (f as TemplateField).key);
    if (new Set(keys).size !== keys.length) return 'Two fields have the same key.';
  }
  // Every {placeholder} must be a field, or it would reach the agent as literal braces.
  if (keys || !partial) {
    const known = new Set(keys ?? []);
    for (const k of ['goal', 'prompt', 'missionTitle', 'branch'] as const) {
      const text = v[k];
      if (typeof text !== 'string') continue;
      const missing = placeholdersIn(text).find((p) => !known.has(p));
      if (missing) return `The ${k === 'missionTitle' ? 'mission title' : k} uses {${missing}}, which is not one of the fields.`;
    }
  }
  return null;
}

/** Why the values for a start are not acceptable, or null. */
export function templateValuesError(t: Pick<MissionTemplate, 'fields'>, values: unknown): string | null {
  if (values !== undefined && !isObj(values)) return 'Send the field values as an object.';
  const vals = (values ?? {}) as Record<string, unknown>;
  for (const f of t.fields) {
    const raw = vals[f.key];
    if (raw !== undefined && typeof raw !== 'string') return `${f.label} must be text.`;
    const s = (raw ?? '').trim();
    if (f.required && !s) return `Fill in ${f.label.toLowerCase()}.`;
    if (s.length > TEMPLATE_VALUE_MAX) return `Keep ${f.label.toLowerCase()} under ${TEMPLATE_VALUE_MAX} characters.`;
  }
  return null;
}

export function templateStartError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the start request as a JSON object.';
  if (typeof v.machineId !== 'string' || !v.machineId) return 'Choose a machine to run on.';
  const hasCheckout = typeof v.checkoutId === 'string' && !!v.checkoutId;
  const hasCwd = typeof v.cwd === 'string' && !!v.cwd;
  if (!hasCheckout && !hasCwd) return 'Choose a project checkout to run in.';
  if (v.mode !== undefined && v.mode !== 'headless' && v.mode !== 'terminal') return 'The mode must be headless or terminal.';
  if (v.newWorktree !== undefined && v.newWorktree !== null) {
    if (!isObj(v.newWorktree) || typeof v.newWorktree.branch !== 'string' || !safeBranch(v.newWorktree.branch)) return 'A new worktree needs a branch name.';
  }
  if (v.model !== undefined && (typeof v.model !== 'string' || !/^[\w.:/@-]{1,100}$/.test(v.model))) return 'That model name is not valid.';
  return null;
}
